import { useEffect, useMemo, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import type { Notification } from '@enterprise/api';
import { findIndex, isDefined, isNotDefined } from '@enterprise/core';
import type { OperationResultWithData } from '@enterprise/types';
import { useSse } from '@enterprise/ui';

import type { ApplicationSseEventsMap } from '@/common/sse/application-sse-event-map';

import { NotificationPayloadSchema } from '@/common/sse/schemas/notification-payload.schema';
import { notificationsQueryKeys } from '@feat/notifications/common/notifications-query-keys';
import { useNotifier } from '@hooks/notifier/notifier.hook';

import type { NotificationSseOptions } from './types/use-notification-sse.type';

type NotificationSseEvents = Pick<ApplicationSseEventsMap, 'notification'>;

export const useNotificationSse = ({ onNew, raiseNative }: NotificationSseOptions): void => {
  const queryClient = useQueryClient();
  // Held in a ref so the subscribe effect does not depend on (and re-subscribe to) a
  // fresh notifier object every render; its methods are stateless wrappers over the toast.
  const notifier = useNotifier();
  const notifierRef = useRef(notifier);

  notifierRef.current = notifier;
  const { on } = useSse<NotificationSseEvents>();
  // Stable across renders so the subscribe effect runs once (not on every render).
  const listKey = useMemo(() => {
    return notificationsQueryKeys.list();
  }, []);

  useEffect(() => {
    return on('notification', (payload) => {
      const parsed = NotificationPayloadSchema.safeParse(payload);

      if (!parsed.success) {
        console.warn('Invalid notification SSE payload', parsed.error);

        return;
      }

      // Normalize: the wire omits null fields (parsed as undefined), but Notification uses `| null`.
      const incoming: Notification = {
        body: parsed.data.body,
        createdDate: parsed.data.createdDate,
        entityId: parsed.data.entityId ?? null,
        entityType: parsed.data.entityType ?? null,
        id: parsed.data.id,
        outcome: parsed.data.outcome,
        processRequestId: parsed.data.processRequestId ?? null,
        readDate: parsed.data.readDate ?? null,
        recipientUserId: parsed.data.recipientUserId,
        sourceCode: parsed.data.sourceCode ?? null,
        title: parsed.data.title,
        updatedDate: parsed.data.updatedDate,
      };
      // Typed as boolean (not the `false` literal) because it is mutated inside the setQueryData
      // updater closure, which the type checker cannot track from this outer scope.
      let isNew = false as boolean;

      queryClient.setQueryData<OperationResultWithData<Notification[]>>(listKey, (cached) => {
        // Cache absent — seed it with just this notification
        if (isNotDefined(cached)) {
          isNew = true;

          return { data: [incoming], failure: false, success: true };
        }

        if (cached.failure) {
          return cached;
        }

        const { data } = cached;

        if (isNotDefined(data)) {
          isNew = true;

          return { ...cached, data: [incoming] };
        }

        const index = findIndex(data, 'id', incoming.id);

        if (index >= 0) {
          // In-place update (running→terminal): MERGE — preserve existing createdDate
          const existing = data[index];
          const merged: Notification = {
            // Update all mutable fields
            body: incoming.body,
            // Preserve the immutable field from the cached row
            createdDate: isDefined(existing) ? existing.createdDate : incoming.createdDate,
            entityId: incoming.entityId,
            entityType: incoming.entityType,
            id: incoming.id,
            outcome: incoming.outcome,
            processRequestId: incoming.processRequestId,
            readDate: incoming.readDate,
            recipientUserId: incoming.recipientUserId,
            sourceCode: incoming.sourceCode,
            title: incoming.title,
            updatedDate: incoming.updatedDate,
          };

          return {
            ...cached,
            data: [...data.slice(0, index), merged, ...data.slice(index + 1)],
          };
        }

        // New id — prepend (newest first)
        isNew = true;

        return { ...cached, data: [incoming, ...data] };
      });

      // Toast on every event — a new arrival AND a running→terminal update — to mirror the
      // design (each event surfaces a toast). The id is state-aware (`<id>-<outcome>`) so the
      // terminal toast is not suppressed by the still-visible running toast, while a duplicate
      // delivery of the same state still dedupes.
      notifierRef.current.outcome(incoming.outcome, {
        description: incoming.body,
        id: `${incoming.id}-${incoming.outcome}`,
        title: incoming.title,
      });

      // Ring/bump animation + native OS notification only for genuinely new arrivals.
      if (isNew) {
        onNew(incoming.id);
        raiseNative?.(incoming);
      }
    });
  }, [on, queryClient, listKey, onNew, raiseNative]);
};
